import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Send, AlertCircle, Info, CheckCircle, AlertTriangle, X, Settings2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    case 'alert': return <AlertCircle className="w-5 h-5 text-red-500" />;
    default: return <Info className="w-5 h-5 text-blue-500" />;
  }
};

const NotificationBadge = ({ type }) => {
  const colors = {
    success: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    alert: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${colors[type] || colors.info}`}>
      {type}
    </span>
  );
};

// Full notifications page for each role (admins had no page, so broadcast and history were unreachable).
export function notificationsPathForRole(role) {
  if (role === 'teacher') return '/teacher/notifications';
  if (role === 'student') return '/student/notifications';
  if (role === 'parent') return '/parent/notifications';
  if (role === 'super_admin') return '/admin/notifications';
  return '/institute-panel/notifications';
}

export function NotificationDropdown({ onClose, onUnreadCountChange }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { params: { limit: 10 } });
      setNotifications(res.data.notifications || []);
      const newCount = res.data.unread_count || 0;
      setUnreadCount(newCount);
      if (onUnreadCountChange) onUnreadCountChange(newCount);
    } catch (e) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        if (onUnreadCountChange) onUnreadCountChange(newCount);
        return newCount;
      });
    } catch (e) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      if (onUnreadCountChange) onUnreadCountChange(0);
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) {
      console.error('Failed to delete');
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="absolute right-0 top-12 w-80 md:w-96 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-100 dark:border-slate-800 z-50 animate-scale-in" data-testid="notification-dropdown">
      <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-indigo-600 dark:bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-50 dark:bg-slate-800 rounded-lg animate-pulse" />)}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
            {notifications.map(notif => (
              <div
                key={notif.id}
                className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors ${!notif.is_read ? 'bg-indigo-50/30 dark:bg-slate-800/40' : ''}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <NotificationIcon type={notif.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${!notif.is_read ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                        {notif.title}
                      </p>
                      <span className="text-xs text-slate-400 flex-shrink-0">{formatTime(notif.created_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{notif.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <NotificationBadge type={notif.type} />
                      {!notif.is_read && (
                        <button
                          onClick={() => markAsRead(notif.id)}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Mark read
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notif.id)}
                        className="text-xs text-slate-400 hover:text-red-500 ml-auto"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Link
        to={notificationsPathForRole(user?.role)}
        onClick={() => onClose && onClose()}
        data-testid="notifications-view-all"
        className="block text-center text-xs font-semibold text-indigo-600 hover:bg-indigo-50 py-2.5 border-t border-slate-100 dark:text-indigo-400 dark:border-slate-800 dark:hover:bg-indigo-500/10"
      >
        View all notifications & settings
      </Link>
    </div>
  );
}

// Full page notifications for institute panel
export function NotificationsPage({ DashboardLayout: Layout }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: '', message: '', ntype: 'info' });
  const [filters, setFilters] = useState({ query: '', type: 'all', from: '', to: '' });
  const [pendingFilters, setPendingFilters] = useState({ query: '', type: 'all', from: '', to: '' });

  const canBroadcast = user?.role === 'super_admin' || user?.role === 'institute_admin';

  // Per-user preferences: muted categories get no phone push (promotions are skipped entirely).
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ categories: {}, muted: [] });
  const [retentionDays, setRetentionDays] = useState(0);
  const loadPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`);
      setPrefs({ categories: res.data?.categories || {}, muted: res.data?.muted || [] });
      if (user?.role === 'super_admin') {
        const ret = await axios.get(`${API}/notifications/admin/retention`);
        setRetentionDays(ret.data?.retention_days || 0);
      }
    } catch {
      toast.error('Could not load notification settings');
    }
  };
  const togglePrefs = () => {
    if (!showPrefs) loadPrefs();
    setShowPrefs((v) => !v);
  };
  const savePrefs = async () => {
    try {
      const res = await axios.put(`${API}/notifications/preferences`, { muted: prefs.muted });
      setPrefs((p) => ({ ...p, muted: res.data?.muted || [] }));
      if (user?.role === 'super_admin') {
        await axios.put(`${API}/notifications/admin/retention`, { retention_days: Number(retentionDays) || 0 });
      }
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save settings');
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const params = { limit: 500 };
      if (filters.query) params.search = filters.query;
      if (filters.type && filters.type !== 'all') params.type = filters.type;
      if (filters.from) params.date_from = filters.from;
      if (filters.to) params.date_to = filters.to;

      const res = await axios.get(`${API}/notifications`, { params });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const applyFilters = () => {
    setLoading(true);
    setFilters(pendingFilters);
  };

  const resetFilters = () => {
    setPendingFilters({ query: '', type: 'all', from: '', to: '' });
    setFilters({ query: '', type: 'all', from: '', to: '' });
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notifications/broadcast`, null, {
        params: { title: broadcastForm.title, message: broadcastForm.message, ntype: broadcastForm.ntype }
      });
      toast.success('Broadcast sent to all users!');
      setShowBroadcast(false);
      setBroadcastForm({ title: '', message: '', ntype: 'info' });
      fetchNotifications();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send broadcast');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch (e) {
      toast.error('Failed');
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Layout title="Notifications">
      <div className="space-y-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 dark:bg-indigo-950/60 p-2.5 rounded-2xl">
              <Bell className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-xl text-slate-900 dark:text-white">All Notifications</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{unreadCount} unread notification{unreadCount === 1 ? '' : 's'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-750 transition-colors cursor-pointer">
                <CheckCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Mark All Read
              </button>
            )}
            <button
              onClick={togglePrefs}
              data-testid="notification-prefs-btn"
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <Settings2 className="w-4 h-4" /> Notification settings
            </button>
            {canBroadcast && (
              <button
                onClick={() => setShowBroadcast(true)}
                data-testid="broadcast-btn"
                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer shadow-xs"
              >
                <Send className="w-4 h-4" /> Send Broadcast
              </button>
            )}
          </div>
        </div>

        {showPrefs && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 dark:border-slate-800 dark:bg-slate-900 shadow-xs" data-testid="notification-prefs">
            <div>
              <p className="font-semibold text-sm text-slate-900 dark:text-white">Which notifications should reach your phone?</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Turned-off topics still appear here in the bell, but your phone will not buzz for them. Turning off offers hides promotional messages completely.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(prefs.categories).map(([key, label]) => {
                const on = !prefs.muted.includes(key);
                return (
                  <label key={key} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 cursor-pointer">
                    <input type="checkbox" checked={on}
                      onChange={(e) => setPrefs((p) => ({ ...p, muted: e.target.checked ? p.muted.filter((m) => m !== key) : [...p.muted, key] }))} />
                    {label}
                  </label>
                );
              })}
            </div>
            {user?.role === 'super_admin' && (
              <label className="block text-xs text-slate-600 dark:text-slate-300">
                Keep read notifications for (days, 0 = forever) — platform-wide
                <input type="number" min="0" max="3650" value={retentionDays}
                  onChange={(e) => setRetentionDays(Math.max(0, Math.min(3650, parseInt(e.target.value, 10) || 0)))}
                  className="mt-1 block w-40 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white" />
                <span className="block mt-1 text-slate-400">Unread notifications are never removed. Cleanup runs daily at night.</span>
              </label>
            )}
            <button onClick={savePrefs} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-indigo-700 cursor-pointer shadow-xs">Save settings</button>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr]">
          <input
            type="text"
            value={pendingFilters.query}
            onChange={e => setPendingFilters(prev => ({ ...prev, query: e.target.value }))}
            placeholder="Search by title or message"
            className="w-full h-11 px-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10"
          />
          <select
            value={pendingFilters.type}
            onChange={e => setPendingFilters(prev => ({ ...prev, type: e.target.value }))}
            className="w-full h-11 px-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10"
          >
            <option value="all">All Types</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="alert">Alert</option>
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={pendingFilters.from}
              onChange={e => setPendingFilters(prev => ({ ...prev, from: e.target.value }))}
              className="w-full h-11 px-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10"
            />
            <input
              type="date"
              value={pendingFilters.to}
              onChange={e => setPendingFilters(prev => ({ ...prev, to: e.target.value }))}
              className="w-full h-11 px-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={applyFilters}
            className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer shadow-xs"
          >
            Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-750 transition-colors cursor-pointer"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5 h-20 animate-pulse" />)}
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <Bell className="w-16 h-16 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">No Notifications</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm">No notifications matched your filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map(notif => (
            <div
              key={notif.id}
              className={`bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-4 flex items-start gap-4 shadow-xs transition-all hover:shadow-md ${!notif.is_read ? 'border-l-4 border-l-indigo-500 bg-indigo-50/20 dark:bg-indigo-950/20' : ''}`}
              data-testid={`notif-page-${notif.id}`}
            >
              <NotificationIcon type={notif.type} />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <h4 className={`text-sm ${!notif.is_read ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-700 dark:text-slate-300'}`}>{notif.title}</h4>
                  <span className="text-xs text-slate-400 flex-shrink-0">{formatTime(notif.created_at)}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{notif.message}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <NotificationBadge type={notif.type} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showBroadcast && canBroadcast && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-outfit font-bold text-lg text-slate-900 dark:text-white">Send Broadcast</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Send notification to all students, teachers, and parents</p>
            </div>
            <form onSubmit={handleBroadcast} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Title *</label>
                <input
                  value={broadcastForm.title}
                  onChange={e => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                  required
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                  placeholder="e.g., Holiday Announcement"
                  data-testid="broadcast-title"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Message *</label>
                <textarea
                  value={broadcastForm.message}
                  onChange={e => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                  required
                  rows={3}
                  className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 resize-none"
                  placeholder="Your message..."
                  data-testid="broadcast-message"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1 block">Type</label>
                <div className="flex gap-2">
                  {['info', 'success', 'warning', 'alert'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setBroadcastForm({ ...broadcastForm, ntype: t })}
                      className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize transition-all cursor-pointer ${
                        broadcastForm.ntype === t ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBroadcast(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-750 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-broadcast"
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                >
                  <Send className="w-4 h-4" /> Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default NotificationsPage;
